'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useTimeTracking } from '@/lib/stores/time-tracking-store'
import { TimerWarningModal } from '@/components/time/timer-warning-modal'
import { useAuth } from '@/lib/stores/auth-store'
import { toast } from '@/hooks/use-toast'

export function TimerProtection() {
  const { activeTimer, getFormattedTime, stopTimer, getTotalHours } = useTimeTracking()
  const { user } = useAuth()
  const router = useRouter()
  const [showWarning, setShowWarning] = useState(false)
  const [pendingAction, setPendingAction] = useState<'close' | 'logout' | null>(null)

  // Note: Cross-tab sync is handled automatically by Zustand persist middleware

  // Note: beforeunload event handler removed to allow free page reloads
  // Timer state is persisted in localStorage, so it survives reloads
  // If you want to re-enable reload warnings, uncomment the code below:
  /*
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only prevent if timer is active and not paused
      if (activeTimer && !activeTimer.isPaused) {
        // Standard warning message for tab close/refresh
        e.preventDefault()
        e.returnValue = 'You have an active timer running. Are you sure you want to leave?'
        return e.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [activeTimer])
  */

  useEffect(() => {
    const handleLogout = (event: Event) => {
      if (activeTimer && !activeTimer.isPaused) {
        event.preventDefault()
        setPendingAction('logout')
        setShowWarning(true)
      }
    }

    window.addEventListener('app:logout', handleLogout)

    return () => {
      window.removeEventListener('app:logout', handleLogout)
    }
  }, [activeTimer])

  const handleStopAndLog = async () => {
    if (!activeTimer) return

    const timer = stopTimer()
    if (!timer) return

    const hours = getTotalHours()

    try {
            const response = await fetch('/api/time_entries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          time_entry: {
            issue_id: timer.issueId,
            project_id: timer.projectId,
            hours: hours,
            activity_id: timer.activityId,
            comments: timer.description || 'Time tracked via OrbitFlow',
            spent_on: new Date().toISOString().split('T')[0]
          }
        })
      })

      if (response.ok) {
        setShowWarning(false)

        // Proceed with pending action
        if (pendingAction === 'logout') {
          window.location.href = '/api/auth/logout'
        } else if (pendingAction === 'close') {
          window.close()
        }
      } else {
        toast({ title: 'Error', description: 'Failed to log time. Please try again.', variant: 'destructive' })
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error logging time. Please try again.', variant: 'destructive' })
    }
  }

  const handleContinue = () => {
    setShowWarning(false)

        if (pendingAction === 'logout') {
      window.location.href = '/api/auth/logout'
    } else if (pendingAction === 'close') {
      window.close()
    }

    setPendingAction(null)
  }

  const handleCancel = () => {
    setShowWarning(false)
    setPendingAction(null)
  }

  if (!activeTimer) return null

  return (
    <TimerWarningModal
      isOpen={showWarning}
      onClose={handleCancel}
      onStopAndLog={handleStopAndLog}
      onContinue={handleContinue}
      formattedTime={getFormattedTime()}
      issueSubject={activeTimer.issueSubject}
    />
  )
}
